import React from "react";
import { Search, X } from "lucide-react";

/**
 * Slim list-search input used across Sales & Purchases pages.
 *
 * Usage:
 *   const [q, setQ] = useState("");
 *   const filtered = useMemo(() => filterBy(items, q, ["name", "email"]), [items, q]);
 *   <ListSearch value={q} onChange={setQ} placeholder="Search customers…" testId="customers-search" />
 */
export default function ListSearch({ value, onChange, placeholder = "Search…", testId }) {
    return (
        <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
                type="search"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testId}
                className="w-full h-10 pl-9 pr-9 rounded-xl border border-sand-300 bg-white text-sm text-ocean-700 placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary transition-colors"
            />
            {value && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-muted-foreground hover:text-ocean-700 hover:bg-sand-100 transition-colors"
                    aria-label="Clear search"
                    data-testid={testId ? `${testId}-clear` : undefined}
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
}

/**
 * Case-insensitive OR-match across a list of string fields.
 * Returns items untouched when query is empty.
 */
/**
 * Case-insensitive OR-match across a list of string fields. Each entry can be
 * either a top-level field name (string) or an accessor function that returns
 * the searchable text for that item — handy for nested arrays like invoice
 * line items.
 *
 * Returns items untouched when query is empty.
 */
export function filterBy(items, query, fields) {
    if (!query || !query.trim()) return items;
    const s = query.toLowerCase().trim();
    return (items || []).filter((it) => fields.some((f) => {
        const val = typeof f === "function" ? f(it) : it?.[f];
        if (val == null) return false;
        return String(val).toLowerCase().includes(s);
    }));
}
